'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';
import type {
  QBReportResponse,
  QBProfitLossReport,
  IntuitReportRow,
} from '@/lib/quickbooks/types';

export type PLPeriod = 'MTD' | 'LastMonth' | 'QTD' | 'YTD';

interface ReportState<T> {
  data: QBReportResponse<T> | null;
  loading: boolean;
  error: string | null;
}

interface Props {
  profitLoss: ReportState<QBProfitLossReport>;
  period: PLPeriod;
  onPeriodChange: (p: PLPeriod) => void;
  onRetry: () => void;
}

const PERIOD_LABEL: Record<PLPeriod, string> = {
  MTD: 'Month to date',
  LastMonth: 'Last month',
  QTD: 'Quarter to date',
  YTD: 'Year to date',
};

// ── Parse: flatten Intuit row tree into a flat list of (label, depth, value, isTotal) entries
interface FlatRow {
  label: string;
  value: number;
  depth: number;
  isTotal: boolean;
}

function flattenPL(report: QBProfitLossReport | undefined): FlatRow[] {
  if (!report?.Rows?.Row) return [];
  const out: FlatRow[] = [];
  const walk = (rows: IntuitReportRow[], depth: number) => {
    for (const row of rows) {
      const headerLabel = row.Header?.ColData?.[0]?.value;
      if (headerLabel) {
        out.push({ label: headerLabel, value: 0, depth, isTotal: false });
      }
      if (row.Rows?.Row) walk(row.Rows.Row, depth + 1);
      if (row.type === 'Data' && row.ColData) {
        const label = row.ColData[0]?.value ?? '';
        const value = parseFloat(row.ColData[1]?.value ?? '0') || 0;
        if (label) out.push({ label, value, depth, isTotal: false });
      }
      const sumLabel = row.Summary?.ColData?.[0]?.value;
      const sumVal   = row.Summary?.ColData?.[1]?.value;
      if (sumLabel && sumVal !== undefined) {
        out.push({ label: sumLabel, value: parseFloat(sumVal) || 0, depth, isTotal: true });
      }
    }
  };
  walk(report.Rows.Row, 0);
  return out;
}

function formatMoney(val: number): string {
  const abs = Math.abs(val);
  const formatted = abs >= 1000
    ? abs.toLocaleString('en-US', { maximumFractionDigits: 0 })
    : abs.toFixed(0);
  return `${val < 0 ? '-' : ''}$${formatted}`;
}

function staleLabel(fetchedAt: string): string {
  const mins = Math.floor((Date.now() - new Date(fetchedAt).getTime()) / 60_000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h`;
}

export default function Row3PLThisMonth({ profitLoss, period, onPeriodChange, onRetry }: Props) {
  const isLoading = profitLoss.loading && !profitLoss.data;
  const isError = !profitLoss.loading && profitLoss.error && !profitLoss.data;
  const rows = flattenPL(profitLoss.data?.data);
  const mock = !!profitLoss.data?.mock;
  const stale = !!profitLoss.data?.staleOnError;

  return (
    <div
      className="relative rounded-xl p-5"
      style={{
        background: '#FFFFFF',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid #F0F0F0',
      }}
    >
      {mock && <DemoDataPill />}
      <div className="flex items-center justify-between gap-2 mb-1 pr-16">
        <div className="flex items-center gap-2">
          <Icon name="ChartBarIcon" size={15} style={{ color: '#033494' }} />
          <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
            P&amp;L · {PERIOD_LABEL[period]}
          </h3>
        </div>
        <select
          value={period}
          onChange={(e) => onPeriodChange(e.target.value as PLPeriod)}
          className="rounded-lg px-2.5 py-1.5 text-xs font-semibold cursor-pointer"
          style={{ background: '#F3F4F6', color: '#000150', border: '1px solid #E5E7EB' }}
        >
          <option value="MTD">Month to date</option>
          <option value="LastMonth">Last month</option>
          <option value="QTD">Quarter to date</option>
          <option value="YTD">Year to date</option>
        </select>
      </div>
      {stale && (
        <p className="text-[10px] italic text-gray-400 mb-2 pr-16">
          Data may be stale · refreshed {staleLabel(profitLoss.data!.fetchedAt)} ago
        </p>
      )}

      {isLoading ? (
        <div className="py-8 flex items-center justify-center">
          <div className="w-6 h-6 rounded-full border-[2.5px] border-[#07B0E9] border-t-transparent animate-spin" />
        </div>
      ) : isError ? (
        <div
          className="rounded-lg p-3 flex items-start gap-2"
          style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.18)' }}
        >
          <Icon name="ExclamationTriangleIcon" size={16} style={{ color: '#B91C1C', flexShrink: 0 }} />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold" style={{ color: '#7F1D1D' }}>Unable to load P&amp;L</p>
            <p className="text-[11px] text-gray-600 mt-0.5 break-words">{profitLoss.error}</p>
            <button
              onClick={onRetry}
              className="mt-2 rounded-md px-2.5 py-1 text-[11px] font-semibold text-white hover:opacity-90 active:scale-95 transition"
              style={{ background: '#EF4444' }}
            >
              Retry
            </button>
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-lg p-3 flex items-center gap-2"
          style={{ background: 'rgba(107,114,128,0.06)', border: '1px solid rgba(107,114,128,0.18)' }}
        >
          <Icon name="DocumentTextIcon" size={16} style={{ color: '#6B7280' }} />
          <p className="text-xs font-medium text-gray-600">No P&amp;L data for the selected period</p>
        </div>
      ) : (
        <div className="mt-2 max-h-[420px] overflow-y-auto">
          <table className="w-full text-xs">
            <tbody className="divide-y divide-gray-50">
              {rows.map((r, i) => (
                <tr
                  key={`${r.label}-${i}`}
                  className={r.isTotal ? 'font-bold' : ''}
                  style={r.isTotal ? { background: 'rgba(248,250,252,0.7)' } : undefined}
                >
                  <td
                    className="px-3 py-1.5"
                    style={{
                      paddingLeft: `${12 + r.depth * 12}px`,
                      color: r.isTotal ? '#000150' : '#374151',
                    }}
                  >
                    {r.label}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono" style={{ color: r.isTotal ? '#000150' : '#374151' }}>
                    {r.value !== 0 || r.isTotal ? formatMoney(r.value) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
